import { useState, useEffect, useMemo, useCallback } from 'react'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  ArrowLeft,
  Save,
  Loader2,
  AlertCircle,
  BookOpen,
  FolderOpen,
  PlayCircle,
  ChevronRight,
  ChevronDown,
  Plus,
  X,
  GripVertical,
  CheckCircle2,
  Clock,
  FileText,
  Eye,
} from 'lucide-react'
import {
  printBookApi,
  type PrintBookModuleRequest,
  type PrintBookCreateRequest,
  type PrintBookUpdateRequest,
} from '@/api/printBook'
import { adminApi, type AdminContent } from '@/api/admin'

// ─── Types ──────────────────────────────────────────────────────────

interface BookForm {
  title: string
  subtitle: string
  bookLabel: string
  description: string
}

interface SelectedModule {
  moduleId: number
  moduleName: string
  sortOrder: number
  moduleSummaryStart?: number
  lessons: SelectedLesson[]
}

interface SelectedLesson {
  lessonId: number
  lessonName: string
  sortOrder: number
  pageCount: number | null
  startPage: number | null
  hasMemoPage: boolean
  confirmed: boolean
}

const DEFAULT_FORM: BookForm = {
  title: '',
  subtitle: '',
  bookLabel: '',
  description: '',
}

const TOC_PAGE_COUNT = 2

// ─── Page Calculation ───────────────────────────────────────────────

// 페이지 구조:
// p.1: 속표지 (1p)
// p.2~: 목차 (tocPageCount)
// 모듈마다: 모듈 요약 (2p) + 레슨들
// 레슨은 항상 짝수 페이지(왼쪽)에서 시작, 필요 시 메모 페이지 삽입
const COVER_PAGE_COUNT = 1
const MODULE_SUMMARY_PAGE_COUNT = 2

function calculatePages(tocPageCount: number, modules: SelectedModule[]): SelectedModule[] {
  let currentPage = COVER_PAGE_COUNT + tocPageCount + 1 // 속표지 + 목차 이후

  return modules.map(mod => {
    // 모듈 요약 2페이지
    // 모듈 요약은 짝수에서 시작해야 첫 레슨이 짝수에서 시작
    if (currentPage % 2 !== 0) currentPage++ // 홀수면 패딩
    const moduleSummaryStart = currentPage
    currentPage += MODULE_SUMMARY_PAGE_COUNT // 2페이지

    return {
      ...mod,
      moduleSummaryStart,
      lessons: mod.lessons.map(lesson => {
        // 레슨은 항상 짝수 페이지에서 시작
        if (currentPage % 2 !== 0) currentPage++ // 홀수면 더미 삽입

        const startPage = currentPage
        const pageCount = lesson.pageCount || 0
        const endPage = startPage + pageCount - 1
        currentPage = endPage + 1

        // 더미 필요 여부: 레슨이 홀수에서 끝나면 다음이 짝수 → 더미 불필요
        // 레슨이 짝수에서 끝나면 다음이 홀수 → 더미 필요
        const hasMemoPage = pageCount > 0 && (endPage % 2 === 0)
        if (hasMemoPage) currentPage++ // 메모 페이지 포함

        return {
          ...lesson,
          startPage,
          hasMemoPage,
        }
      })
    }
  })
}

function getTotalPageCount(tocPageCount: number, modules: SelectedModule[]): number {
  const calculated = calculatePages(tocPageCount, modules)
  let maxPage = COVER_PAGE_COUNT + tocPageCount
  calculated.forEach(mod => {
    // 모듈 요약 페이지
    if (mod.moduleSummaryStart) {
      const modEnd = mod.moduleSummaryStart + MODULE_SUMMARY_PAGE_COUNT - 1
      if (modEnd > maxPage) maxPage = modEnd
    }
    mod.lessons.forEach(lesson => {
      if (lesson.startPage && lesson.pageCount) {
        const end = lesson.startPage + lesson.pageCount - 1 + (lesson.hasMemoPage ? 1 : 0)
        if (end > maxPage) maxPage = end
      }
    })
  })
  return maxPage
}

// ─── Content Tree ───────────────────────────────────────────────────

function ContentTree({ onSelectModule }: {
  onSelectModule: (moduleId: number, moduleName: string, lessons: { id: number; name: string }[]) => void
}) {
  const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set())

  const { data: contents, isLoading } = useQuery({
    queryKey: ['admin-contents-tree', 'curriculum'],
    queryFn: () => adminApi.getContentsByLevel('curriculum'),
  })

  const toggleExpand = (id: number) => {
    setExpandedIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  if (isLoading) return <div className="p-4 text-sm text-slate-400"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />로딩 중...</div>

  return (
    <div className="text-sm">
      {contents?.map((curr: AdminContent) => (
        <ContentNode
          key={curr.id}
          content={curr}
          level={0}
          expandedIds={expandedIds}
          onToggle={toggleExpand}
          onSelectModule={onSelectModule}
        />
      ))}
    </div>
  )
}

function ContentNode({ content, level, expandedIds, onToggle, onSelectModule }: {
  content: AdminContent
  level: number
  expandedIds: Set<number>
  onToggle: (id: number) => void
  onSelectModule: (moduleId: number, moduleName: string, lessons: { id: number; name: string }[]) => void
}) {
  const isExpanded = expandedIds.has(content.id)
  const isModule = content.hierarchyLevel === 'module'
  const isLesson = content.hierarchyLevel === 'lesson'
  const canExpand = !isLesson

  const { data: children } = useQuery({
    queryKey: ['admin-content-children', content.id],
    queryFn: () => adminApi.getChildContents(content.id),
    enabled: isExpanded && canExpand,
  })

  const Icon = isLesson ? PlayCircle : isModule ? FolderOpen : BookOpen

  const handleAddModule = async () => {
    if (!isModule) return
    // Fetch children if not loaded yet
    const lessonData = children || await adminApi.getChildContents(content.id)
    const lessons = lessonData.map((c: AdminContent) => ({
      id: c.id,
      name: c.title,
    }))
    onSelectModule(content.id, content.title, lessons)
  }

  return (
    <div>
      <div
        className="flex items-center gap-1.5 py-1.5 px-2 rounded hover:bg-slate-50 cursor-pointer"
        style={{ paddingLeft: `${level * 16 + 8}px` }}
        onClick={() => canExpand ? onToggle(content.id) : undefined}
      >
        {canExpand ? (
          isExpanded ? <ChevronDown className="w-3.5 h-3.5 text-slate-400 shrink-0" /> : <ChevronRight className="w-3.5 h-3.5 text-slate-400 shrink-0" />
        ) : (
          <span className="w-3.5 shrink-0" />
        )}
        <Icon className="w-3.5 h-3.5 text-slate-400 shrink-0" />
        <span className="truncate text-slate-700">{content.title}</span>
        {isModule && (
          <button
            onClick={(e) => {
              e.stopPropagation()
              handleAddModule()
            }}
            className="ml-auto p-0.5 text-amber-500 hover:bg-amber-50 rounded shrink-0"
            title="모듈 추가"
          >
            <Plus className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
      {isExpanded && children?.map((child: AdminContent) => (
        <ContentNode
          key={child.id}
          content={child}
          level={level + 1}
          expandedIds={expandedIds}
          onToggle={onToggle}
          onSelectModule={onSelectModule}
        />
      ))}
    </div>
  )
}

// ─── Main Component ─────────────────────────────────────────────────

export function BookEditPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const isEdit = !!id

  const [form, setForm] = useState<BookForm>({ ...DEFAULT_FORM })
  const [modules, setModules] = useState<SelectedModule[]>([])
  const [searchParams, setSearchParams] = useSearchParams()
  const [step, setStepState] = useState(() => {
    const tabParam = searchParams.get('tab')
    return tabParam ? Number(tabParam) : 1
  })
  const setStep = (n: number) => {
    setStepState(n)
    setSearchParams({ tab: String(n) }, { replace: true })
  }

  // Load existing book
  const { data: book, isLoading: bookLoading } = useQuery({
    queryKey: ['print-book', id],
    queryFn: () => printBookApi.getById(Number(id)),
    enabled: isEdit,
    refetchOnMount: 'always',
  })

  // book 데이터 변경 시 로컬 state 동기화 (updatedAt 포함하여 변경 감지)
  const bookUpdatedAt = book?.updatedAt
  useEffect(() => {
    if (book) {
      setForm({
        title: book.title,
        subtitle: book.subtitle || '',
        bookLabel: book.bookLabel,
        description: book.description || '',
      })
      setModules(book.modules.map(m => ({
        moduleId: m.moduleId,
        moduleName: m.moduleName || `Module ${m.moduleId}`,
        sortOrder: m.sortOrder,
        lessons: m.lessons.map(l => ({
          lessonId: l.lessonId,
          lessonName: l.lessonName || `Lesson ${l.lessonId}`,
          sortOrder: l.sortOrder,
          pageCount: l.pageCount,
          startPage: l.startPage,
          hasMemoPage: l.hasMemoPage,
          confirmed: l.confirmed,
        }))
      })))
    }
  }, [book, bookUpdatedAt])

  const calculatedModules = useMemo(
    () => calculatePages(TOC_PAGE_COUNT, modules),
    [TOC_PAGE_COUNT, modules]
  )
  const totalPages = useMemo(
    () => getTotalPageCount(TOC_PAGE_COUNT, modules),
    [TOC_PAGE_COUNT, modules]
  )

  // Mutations
  const saveMutation = useMutation({
    mutationFn: async () => {
      const moduleRequests: PrintBookModuleRequest[] = modules.map((m, mi) => ({
        moduleId: m.moduleId,
        sortOrder: mi,
        lessons: m.lessons.map((l, li) => ({
          lessonId: l.lessonId,
          sortOrder: li,
        }))
      }))

      if (isEdit) {
        const req: PrintBookUpdateRequest = {
          ...form,
          subtitle: form.subtitle || undefined,
          description: form.description || undefined,
          modules: moduleRequests,
        }
        return printBookApi.update(Number(id), req)
      } else {
        const req: PrintBookCreateRequest = {
          ...form,
          subtitle: form.subtitle || undefined,
          description: form.description || undefined,
          modules: moduleRequests,
        }
        return printBookApi.create(req)
      }
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['print-books'] })
      queryClient.invalidateQueries({ queryKey: ['print-book', id] })
      if (!isEdit) {
        navigate(`/contents/books/${data.id}`, { replace: true })
      }
    },
  })

  const confirmMutation = useMutation({
    mutationFn: ({ lessonId, confirmed }: { lessonId: number; confirmed: boolean }) =>
      printBookApi.confirmLesson(Number(id), lessonId, confirmed),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['print-book', id] })
    },
  })

  const handleAddModule = useCallback((moduleId: number, moduleName: string, lessons: { id: number; name: string }[]) => {
    if (modules.some(m => m.moduleId === moduleId)) return // already added
    setModules(prev => [...prev, {
      moduleId,
      moduleName,
      sortOrder: prev.length,
      lessons: lessons.map((l, i) => ({
        lessonId: l.id,
        lessonName: l.name,
        sortOrder: i,
        pageCount: null,
        startPage: null,
        hasMemoPage: false,
        confirmed: false,
      }))
    }])
  }, [modules])

  const handleRemoveModule = (moduleId: number) => {
    setModules(prev => prev.filter(m => m.moduleId !== moduleId))
  }

  const handleFormChange = <K extends keyof BookForm>(key: K, value: BookForm[K]) => {
    setForm(prev => ({ ...prev, [key]: value }))
  }

  if (bookLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    )
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/contents/books')} className="p-2 hover:bg-slate-100 rounded-lg">
            <ArrowLeft className="w-5 h-5 text-slate-600" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">
              {isEdit ? form.title || '책 편집' : '새 책 만들기'}
            </h1>
            <p className="text-sm text-slate-500">
              {isEdit ? `총 ${totalPages}페이지` : '기본 정보를 입력하고 모듈을 선택하세요'}
            </p>
          </div>
        </div>
        <button
          onClick={() => saveMutation.mutate()}
          disabled={saveMutation.isPending || !form.title}
          className="flex items-center gap-2 px-4 py-2.5 bg-amber-500 text-slate-900 rounded-lg text-sm font-medium hover:bg-amber-400 disabled:opacity-50 transition-colors"
        >
          {saveMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          저장
        </button>
      </div>

      {saveMutation.isError && (
        <div className="flex items-center gap-2 p-3 mb-4 text-red-700 bg-red-50 rounded-lg text-sm">
          <AlertCircle className="w-4 h-4" />
          저장에 실패했습니다.
        </div>
      )}

      {/* Steps */}
      <div className="flex gap-1 mb-6">
        {[
          { n: 1, label: '기본 정보' },
          { n: 2, label: '모듈 & 레슨' },
          { n: 3, label: '페이지 요약' },
        ].map(s => (
          <button
            key={s.n}
            onClick={() => setStep(s.n)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              step === s.n
                ? 'bg-amber-500 text-slate-900'
                : 'bg-white text-slate-600 hover:bg-slate-100 border border-slate-200'
            }`}
          >
            {s.n}. {s.label}
          </button>
        ))}
      </div>

      {/* Step 1: Basic Info */}
      {step === 1 && (
        <div className="bg-white rounded-xl border border-slate-200 p-6 max-w-2xl">
          <h2 className="text-lg font-semibold text-slate-900 mb-4">책 기본 정보</h2>
          <div className="space-y-4">
            <label className="block">
              <span className="text-sm font-medium text-slate-700">제목 *</span>
              <input
                type="text"
                value={form.title}
                onChange={e => handleFormChange('title', e.target.value)}
                placeholder="PRIME-A 4학년 1학기"
                className="mt-1 block w-full rounded-lg border-slate-300 text-sm shadow-sm focus:border-amber-500 focus:ring-amber-500"
              />
            </label>
            <label className="block">
              <span className="text-sm font-medium text-slate-700">부제</span>
              <input
                type="text"
                value={form.subtitle}
                onChange={e => handleFormChange('subtitle', e.target.value)}
                placeholder="선택사항"
                className="mt-1 block w-full rounded-lg border-slate-300 text-sm shadow-sm focus:border-amber-500 focus:ring-amber-500"
              />
            </label>
            <div className="grid grid-cols-2 gap-4">
              <label className="block">
                <span className="text-sm font-medium text-slate-700">교재 라벨</span>
                <input
                  type="text"
                  value={form.bookLabel}
                  onChange={e => handleFormChange('bookLabel', e.target.value)}
                  placeholder="PRIME-A"
                  className="mt-1 block w-full rounded-lg border-slate-300 text-sm shadow-sm focus:border-amber-500 focus:ring-amber-500"
                />
              </label>
            </div>
            <label className="block">
              <span className="text-sm font-medium text-slate-700">설명</span>
              <textarea
                value={form.description}
                onChange={e => handleFormChange('description', e.target.value)}
                rows={3}
                className="mt-1 block w-full rounded-lg border-slate-300 text-sm shadow-sm focus:border-amber-500 focus:ring-amber-500"
              />
            </label>
          </div>
        </div>
      )}

      {/* Step 2: Module & Lesson Selection */}
      {step === 2 && (
        <div className="grid grid-cols-2 gap-6">
          {/* Left: Content Tree */}
          <div className="bg-white rounded-xl border border-slate-200">
            <div className="px-4 py-3 border-b border-slate-200">
              <h2 className="text-sm font-semibold text-slate-700">커리큘럼 트리</h2>
              <p className="text-xs text-slate-400">모듈의 + 버튼을 클릭하여 추가</p>
            </div>
            <div className="p-2 max-h-[600px] overflow-y-auto">
              <ContentTree onSelectModule={handleAddModule} />
            </div>
          </div>

          {/* Right: Selected Modules */}
          <div className="bg-white rounded-xl border border-slate-200">
            <div className="px-4 py-3 border-b border-slate-200">
              <h2 className="text-sm font-semibold text-slate-700">
                선택된 구성 ({modules.length}개 모듈, {modules.reduce((sum, m) => sum + m.lessons.length, 0)}개 레슨)
              </h2>
            </div>
            <div className="p-3 max-h-[600px] overflow-y-auto space-y-3">
              {modules.length === 0 ? (
                <div className="text-center py-10 text-sm text-slate-400">
                  왼쪽 트리에서 모듈을 추가하세요
                </div>
              ) : (
                modules.map((mod, mi) => (
                  <div key={mod.moduleId} className="border border-slate-200 rounded-lg">
                    <div className="flex items-center justify-between px-3 py-2 bg-slate-50 rounded-t-lg">
                      <div className="flex items-center gap-2">
                        <GripVertical className="w-3.5 h-3.5 text-slate-300" />
                        <FolderOpen className="w-4 h-4 text-teal-500" />
                        <span className="text-sm font-medium text-slate-700">
                          Module {mi + 1}: {mod.moduleName}
                        </span>
                      </div>
                      <button
                        onClick={() => handleRemoveModule(mod.moduleId)}
                        className="p-1 text-slate-400 hover:text-red-500 rounded"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                    <div className="divide-y divide-slate-100">
                      {mod.lessons.map((lesson, li) => (
                        <div key={lesson.lessonId} className="flex items-center gap-2 px-3 py-1.5 text-sm">
                          <PlayCircle className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
                          <span className="text-slate-600 truncate">
                            L{String(li + 1).padStart(2, '0')}. {lesson.lessonName}
                          </span>
                          {lesson.confirmed && (
                            <CheckCircle2 className="w-3.5 h-3.5 text-green-500 ml-auto shrink-0" />
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {/* Step 3: Page Summary */}
      {step === 3 && (
        <div className="bg-white rounded-xl border border-slate-200">
          <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
            <h2 className="text-sm font-semibold text-slate-700">페이지 구성 요약</h2>
            <div className="flex items-center gap-4 text-sm">
              <span className="text-slate-500">총 <strong className="text-slate-900">{totalPages}</strong>페이지</span>
              <span className="text-slate-500">
                컨펌: <strong className="text-slate-900">
                  {modules.flatMap(m => m.lessons).filter(l => l.confirmed).length}
                  /{modules.flatMap(m => m.lessons).length}
                </strong>
              </span>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="px-4 py-2 text-left font-semibold text-slate-500">구분</th>
                  <th className="px-4 py-2 text-center font-semibold text-slate-500">페이지 수</th>
                  <th className="px-4 py-2 text-center font-semibold text-slate-500">시작</th>
                  <th className="px-4 py-2 text-center font-semibold text-slate-500">끝</th>
                  <th className="px-4 py-2 text-center font-semibold text-slate-500">컨펌</th>
                  <th className="px-4 py-2 text-center font-semibold text-slate-500">액션</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {/* 속표지 Row */}
                <tr className="bg-purple-50/50">
                  <td className="px-4 py-2 font-medium text-slate-700 flex items-center gap-2">
                    <BookOpen className="w-4 h-4 text-purple-400" />
                    속표지
                  </td>
                  <td className="px-4 py-2 text-center text-slate-600">1</td>
                  <td className="px-4 py-2 text-center text-slate-600">1</td>
                  <td className="px-4 py-2 text-center text-slate-600">1</td>
                  <td className="px-4 py-2 text-center">-</td>
                  <td className="px-4 py-2 text-center">-</td>
                </tr>

                {/* 목차 Row */}
                <tr className="bg-blue-50/50">
                  <td className="px-4 py-2 font-medium text-slate-700 flex items-center gap-2">
                    <FileText className="w-4 h-4 text-blue-400" />
                    목차
                  </td>
                  <td className="px-4 py-2 text-center text-slate-600">{TOC_PAGE_COUNT}</td>
                  <td className="px-4 py-2 text-center text-slate-600">2</td>
                  <td className="px-4 py-2 text-center text-slate-600">{1 + TOC_PAGE_COUNT}</td>
                  <td className="px-4 py-2 text-center">-</td>
                  <td className="px-4 py-2 text-center">
                    <button
                      onClick={() => {
                        const params = new URLSearchParams({
                          type: 'toc',
                          bookTitle: form.title,
                          bookLabel: form.bookLabel,
                          modules: JSON.stringify(calculatedModules.map(mod => ({
                            moduleName: mod.moduleName,
                            lessons: mod.lessons.map(l => ({
                              lessonName: l.lessonName,
                              startPage: l.startPage,
                            })),
                          }))),
                        })
                        navigate(`/contents/books/${id || 'new'}/preview/toc?${params}`)
                      }}
                      className="inline-flex items-center gap-1 px-2 py-1 text-xs text-blue-600 hover:bg-blue-50 rounded"
                    >
                      <Eye className="w-3 h-3" /> 프리뷰
                    </button>
                  </td>
                </tr>

                {calculatedModules.map((mod, mi) => (
                  <>
                    {/* Module header */}
                    <tr key={`mod-${mod.moduleId}`} className="bg-teal-50/50">
                      <td colSpan={6} className="px-4 py-2 font-medium text-teal-700 flex items-center gap-2">
                        <FolderOpen className="w-4 h-4 text-teal-500" />
                        Module {mi + 1}: {mod.moduleName}
                      </td>
                    </tr>

                    {/* Module summary pages */}
                    {mod.moduleSummaryStart && (
                      <tr key={`mod-summary-${mod.moduleId}`} className="bg-indigo-50/30">
                        <td className="px-4 py-1.5 text-slate-500 pl-10 text-sm">모듈 요약</td>
                        <td className="px-4 py-1.5 text-center text-slate-500 text-sm">2</td>
                        <td className="px-4 py-1.5 text-center text-slate-500 text-sm">{mod.moduleSummaryStart}</td>
                        <td className="px-4 py-1.5 text-center text-slate-500 text-sm">{mod.moduleSummaryStart + 1}</td>
                        <td className="px-4 py-1.5 text-center">-</td>
                        <td className="px-4 py-1.5 text-center">
                          <button
                            onClick={() => {
                              const params = new URLSearchParams({
                                type: 'module-summary',
                                moduleIndex: String(mi),
                                moduleName: mod.moduleName,
                                startPage: String(mod.moduleSummaryStart),
                                bookLabel: form.bookLabel,
                                lessons: JSON.stringify(mod.lessons.map(l => ({
                                  lessonName: l.lessonName,
                                  pageCount: l.pageCount,
                                }))),
                              })
                              navigate(`/contents/books/${id || 'new'}/preview/module-${mod.moduleId}?${params}`)
                            }}
                            className="inline-flex items-center gap-1 px-2 py-1 text-xs text-blue-600 hover:bg-blue-50 rounded"
                          >
                            <Eye className="w-3 h-3" /> 프리뷰
                          </button>
                        </td>
                      </tr>
                    )}

                    {mod.lessons.map((lesson, li) => {
                      const endPage = (lesson.startPage || 0) + (lesson.pageCount || 0) - 1
                      return (
                        <>
                          <tr key={`lesson-${lesson.lessonId}`} className="hover:bg-slate-50">
                            <td className="px-4 py-2 text-slate-600 pl-10">
                              L{String(li + 1).padStart(2, '0')}. {lesson.lessonName}
                            </td>
                            <td className="px-4 py-2 text-center text-slate-600">
                              <input
                                type="number"
                                min={0}
                                value={lesson.pageCount ?? ''}
                                placeholder="-"
                                onChange={e => {
                                  const val = e.target.value === '' ? null : Number(e.target.value)
                                  setModules(prev => prev.map(m => ({
                                    ...m,
                                    lessons: m.lessons.map(l =>
                                      l.lessonId === lesson.lessonId ? { ...l, pageCount: val } : l
                                    )
                                  })))
                                }}
                                className="w-14 text-center rounded border-slate-300 text-sm py-0.5 focus:border-amber-500 focus:ring-amber-500"
                              />
                            </td>
                            <td className="px-4 py-2 text-center text-slate-600">
                              {lesson.startPage || '-'}
                            </td>
                            <td className="px-4 py-2 text-center text-slate-600">
                              {endPage > 0 ? endPage : '-'}
                            </td>
                            <td className="px-4 py-2 text-center">
                              {isEdit ? (
                                <button
                                  onClick={() => confirmMutation.mutate({
                                    lessonId: lesson.lessonId,
                                    confirmed: !lesson.confirmed,
                                  })}
                                  disabled={confirmMutation.isPending}
                                  className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full ${
                                    lesson.confirmed
                                      ? 'bg-green-100 text-green-700'
                                      : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                                  }`}
                                >
                                  {lesson.confirmed
                                    ? <><CheckCircle2 className="w-3 h-3" /> 완료</>
                                    : <><Clock className="w-3 h-3" /> 미확인</>
                                  }
                                </button>
                              ) : (
                                <span className="text-xs text-slate-300">저장 후 가능</span>
                              )}
                            </td>
                            <td className="px-4 py-2 text-center">
                              <div className="inline-flex gap-1">
                                <button
                                  onClick={() => {
                                    const params = new URLSearchParams({
                                      name: lesson.lessonName,
                                      startPage: String(lesson.startPage || 6),
                                      bookLabel: form.bookLabel,
                                      chapter: String(li + 1).padStart(2, '0'),
                                      grade: '초5-1',
                                      unitLabel: '1단원',
                                      unitName: '자연수의 혼합계산',
                                      quetureTitle: lesson.lessonName,
                                    })
                                    navigate(`/contents/books/${id || 'new'}/preview/${lesson.lessonId}?${params}`)
                                  }}
                                  className="inline-flex items-center gap-1 px-2 py-1 text-xs text-blue-600 hover:bg-blue-50 rounded"
                                >
                                  <Eye className="w-3 h-3" /> 프리뷰
                                </button>
                                <button
                                  onClick={() => {
                                    const params = new URLSearchParams({
                                      name: lesson.lessonName,
                                      startPage: String(lesson.startPage || 6),
                                      bookLabel: form.bookLabel,
                                      quetureTitle: lesson.lessonName,
                                    })
                                    navigate(`/contents/books/${id || 'new'}/answer-key/${lesson.lessonId}?${params}`)
                                  }}
                                  className="inline-flex items-center gap-1 px-2 py-1 text-xs text-green-600 hover:bg-green-50 rounded"
                                >
                                  <FileText className="w-3 h-3" /> 정답&해설
                                </button>
                              </div>
                            </td>
                          </tr>
                          {lesson.hasMemoPage && (
                            <tr key={`dummy-${lesson.lessonId}`} className="bg-slate-50/50">
                              <td className="px-4 py-1 text-xs text-slate-400 pl-10 italic">(메모 페이지)</td>
                              <td className="px-4 py-1 text-center text-xs text-slate-400">1</td>
                              <td className="px-4 py-1 text-center text-xs text-slate-400">{endPage + 1}</td>
                              <td className="px-4 py-1 text-center text-xs text-slate-400">{endPage + 1}</td>
                              <td className="px-4 py-1 text-center">-</td>
                              <td className="px-4 py-1 text-center">-</td>
                            </tr>
                          )}
                        </>
                      )
                    })}
                  </>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

    </div>
  )
}
